import {
  createFileRoute,
  useCanGoBack,
  useRouter,
} from '@tanstack/react-router';
import { format, getDay, parse, startOfDay, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { CircleArrowLeft, Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { useGetEventCalendar } from '~entities/events/api/get-event-calender';
import '~features/events/ui/event-calendar.css';
import {
  getEventCalendarRange,
  isEventCalendarView,
} from '~features/events/ui/event-calendar.helpers';
import {
  DESKTOP_EVENT_CALENDAR_VIEWS,
  EVENT_CALENDAR_VIEW,
  type EventCalendarItem,
  type EventCalendarView,
} from '~features/events/ui/event-calendar.types';
import { MobileEventCalendar } from '~features/events/ui/mobile-event-calendar';

import { Button } from '~shared/shadcn/ui/button';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const today = startOfDay(new Date());

export function EventCalendar() {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const [view, setView] = useState<EventCalendarView>(EVENT_CALENDAR_VIEW.WEEK);
  const [date, setDate] = useState(today);

  const range = getEventCalendarRange(view, date);
  const { data, isLoading, isFetching } = useGetEventCalendar({
    startDate: format(range.start, 'yyyy-MM-dd'),
    endDate: format(range.end, 'yyyy-MM-dd'),
  });

  const showCalendarRefresh = isFetching && data != null;

  if (isLoading && !data) {
    return (
      <div className='flex h-[calc(100vh-10rem)] items-center justify-center'>
        <Loader2Icon className='animate-spin' />
      </div>
    );
  }

  const calendarEvents: Array<EventCalendarItem> =
    data?.data && data.data.length > 0
      ? data.data.map((event) => ({
          title: event.title,
          start: new Date(event.start),
          end: new Date(event.end),
          resourceId: event.resourceId,
          color: event.color,
          days: event.days,
        }))
      : [];

  return (
    <div className='relative min-w-0'>
      <div>
        <header className='sticky left-0 top-0 z-10 flex items-center bg-white p-4 pb-3 sm:p-10 sm:pb-8'>
          <img
            className={'h-5'}
            src={
              'https://storage.googleapis.com/msgsndr/VGZndtOFHrO4NIELBQYo/media/672ba39a6e8a940606bd75a8.svg'
            }
            alt='logo'
          />
        </header>
        <div className='flex items-center gap-2 border-b px-4 pb-4 sm:px-10 sm:pb-8'>
          <Button
            variant={'ghost'}
            className='flex shrink-0 items-center p-2 [&_svg]:size-6'
            disabled={!canGoBack}
            onClick={() => {
              router.history.back();
            }}
          >
            <CircleArrowLeft className='size-6' />
          </Button>
          <h2 className='min-w-0 break-words text-xl font-semibold sm:text-2xl'>
            Events Calendar
          </h2>
        </div>
      </div>
      <div className='event-calendar relative min-w-0 px-4 py-4 sm:h-[calc(100vh-10rem)] sm:px-10 sm:py-8'>
        {showCalendarRefresh ? (
          <div
            className='absolute inset-0 z-20 flex items-center justify-center bg-background/60'
            aria-busy='true'
            aria-live='polite'
          >
            <Loader2Icon className='size-8 animate-spin' />
          </div>
        ) : null}
        <div className='md:hidden'>
          <MobileEventCalendar
            view={view}
            date={date}
            events={calendarEvents}
            onViewChange={setView}
            onDateChange={setDate}
          />
        </div>
        <div className='hidden h-full md:block'>
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor='start'
            endAccessor='end'
            date={date}
            view={view}
            views={DESKTOP_EVENT_CALENDAR_VIEWS}
            onNavigate={setDate}
            onView={(nextView) => {
              if (isEventCalendarView(nextView)) {
                setView(nextView);
              }
            }}
            scrollToTime={new Date(1970, 0, 1, 8, 0, 0)}
            eventPropGetter={(event) => ({
              style: {
                backgroundColor: event.color,
                border: 'none',
                borderRadius: '4px',
                color: 'black',
              },
            })}
          />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_auth/event-calendar')({
  component: EventCalendar,
});
